import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { requireAccessToken } from '../utils/authToken';

const APIURL = process.env.REACT_APP_API_URL;

interface JoinedPlayer {
  player_id: number;
  first_name: string;
  last_name: string;
}

interface Reservation {
  reservation_id: number;
  complex_name: string | null;
  course_name: string | null;
  course_address: string | null;
  reservation_date: string | null;
  tee_time: string | null;
  num_players: number | null;
  num_holes: number | null;
  reservation_name: string | null;
  booking_ref: string | null;
  raw_subject: string | null;
  raw_from: string | null;
  raw_body: string | null;
  raw_html: string | null;
  ai_notes: string | null;
  status: 'active' | 'cancelled' | 'manual';
  round_id: number | null;
  assigned_tournament: { tournament_id: number; tournament_name: string } | null;
  joined_players: JoinedPlayer[];
  changelog_count: number;
  email_count: number;
  updated_at: string | null;
  created_at: string | null;
  booked_by_alias: string | null;
  booked_by_player_id: number | null;
  booked_by_first_name: string | null;
  booked_by_last_name: string | null;
}

interface ChangelogChange {
  field: string;
  field_label: string;
  old_value: string | null;
  new_value: string | null;
}

interface ChangelogEntry {
  email_id: number;
  received_at: string;
  raw_subject: string | null;
  raw_from: string | null;
  raw_body: string | null;
  raw_html: string | null;
  action: 'initial' | 'update' | 'cancellation' | 'no_change';
  changes: ChangelogChange[];
}

interface PlayerOption {
  player_id: number;
  first_name: string;
  last_name: string;
  league_status?: string;
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  cancelled: 'Cancelled',
  manual: 'Manual',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#27ae60',
  cancelled: '#c0392b',
  manual: '#8e44ad',
};

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const AVATAR_COLORS = ['#3498db', '#9b59b6', '#e67e22', '#16a085', '#c0392b', '#8e44ad', '#34495e', '#d35400'];

function parseLocalDate(s: string | null): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// The DB `date` column comes back over JSON as a full ISO timestamp
// (e.g. "2026-05-24T00:00:00.000Z"). Slice to YYYY-MM-DD so day comparisons
// and groupings work regardless of whether the API hands back a Date or a string.
function dateKey(s: string | null): string | null {
  if (!s) return null;
  return s.slice(0, 10);
}

function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function startOfWeek(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  const day = r.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day; // back to Monday
  r.setDate(r.getDate() + diff);
  return r;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function parseTeeTimeMinutes(t: string | null): number {
  if (!t) return 99999;
  const m = t.match(/(\d{1,2}):(\d{2})(?:\s*([AP]M))?/i);
  if (!m) return 99999;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ampm = m[3]?.toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

function splitTime(t: string | null): { time: string; ampm: string | null } {
  if (!t) return { time: '—', ampm: null };
  const m = t.match(/^\s*(\d{1,2}:\d{2})\s*(AM|PM)?/i);
  if (m) return { time: m[1], ampm: m[2] ? m[2].toUpperCase() : null };
  return { time: t, ampm: null };
}

function initials(first: string | null | undefined, last: string | null | undefined): string {
  const a = (first ?? '').trim()[0] ?? '';
  const b = (last ?? '').trim()[0] ?? '';
  return (a + b).toUpperCase() || '?';
}

function avatarColor(id: number): string {
  return AVATAR_COLORS[Math.abs(id) % AVATAR_COLORS.length];
}

function formatWeekRange(weekStart: Date): string {
  const end = addDays(weekStart, 6);
  const sameMonth = weekStart.getMonth() === end.getMonth();
  const sameYear = weekStart.getFullYear() === end.getFullYear();
  const monthShort = (d: Date) => d.toLocaleDateString('en-CA', { month: 'short' });
  if (sameMonth) {
    return `${monthShort(weekStart)} ${weekStart.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  if (sameYear) {
    return `${monthShort(weekStart)} ${weekStart.getDate()} – ${monthShort(end)} ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${monthShort(weekStart)} ${weekStart.getDate()}, ${weekStart.getFullYear()} – ${monthShort(end)} ${end.getDate()}, ${end.getFullYear()}`;
}

export default function Reservations() {
  const { getAccessTokenSilently } = useAuth0();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [courseFilter, setCourseFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [bookedByFilter, setBookedByFilter] = useState<string>('');
  const [joinedPlayerFilter, setJoinedPlayerFilter] = useState<string>('');
  const [hasJoinedFilter, setHasJoinedFilter] = useState(false);

  // Quick filter pills
  const [quickFilter, setQuickFilter] = useState<'all' | 'mine' | 'has_joined' | 'major'>('all');
  const [showCancelled, setShowCancelled] = useState(false);

  // Week navigation
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));

  // Drawer / panel state
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState('');

  // Manual reservation creation modal
  const [showManualModal, setShowManualModal] = useState(false);
  const emptyManualForm = () => ({
    course_name: '',
    complex_name: '',
    facility_id: '',
    course_id: '',
    reservation_date: new Date().toISOString().slice(0, 10),
    tee_hour: '8',
    tee_minute: '00',
    tee_period: 'AM' as 'AM' | 'PM',
    num_players: '4',
    num_holes: '18',
    reservation_name: '',
    booking_ref: '',
    ai_notes: '',
  });
  const [manualForm, setManualForm] = useState(emptyManualForm);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [allPlayers, setAllPlayers] = useState<PlayerOption[]>([]);
  const [myPlayerId, setMyPlayerId] = useState<number | null>(null);

  const token = requireAccessToken(getAccessTokenSilently);

  async function fetchReservations() {
    const t = await token();
    const res = await fetch(`${APIURL}/reservations`, { headers: { Authorization: `Bearer ${t}` } });
    const data = await res.json();
    setReservations(data);
  }

  async function fetchPlayers() {
    const t = await token();
    const res = await fetch(`${APIURL}/players`, { headers: { Authorization: `Bearer ${t}` } });
    // Deactivated (Removed) players aren't selectable for new tee times / rounds.
    if (res.ok) setAllPlayers((await res.json()).filter((p: PlayerOption) => p.league_status !== 'Removed'));
  }

  async function fetchMyProfile() {
    const t = await token();
    const res = await fetch(`${APIURL}/profile`, { headers: { Authorization: `Bearer ${t}` } });
    if (res.ok) {
      const p = await res.json();
      setMyPlayerId(p.player_id || null);
    }
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchReservations(), fetchPlayers(), fetchMyProfile()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReservationsRef = useRef(fetchReservations);
  fetchReservationsRef.current = fetchReservations;

  const courseOptions = useMemo(() => {
    const set = new Set<string>();
    reservations.forEach((r) => {
      const c = r.course_name || r.complex_name;
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [reservations]);

  const bookerOptions = useMemo(() => {
    const map = new Map<number, string>();
    reservations.forEach((r) => {
      if (r.booked_by_player_id && r.booked_by_first_name) {
        map.set(r.booked_by_player_id, `${r.booked_by_first_name} ${r.booked_by_last_name ?? ''}`.trim());
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [reservations]);

  // Apply non-week filters (panel + quick pills)
  const filteredAll = useMemo(() => {
    return reservations.filter((r) => {
      // Hide cancelled by default; the "Show cancelled" toggle or an explicit
      // status=cancelled filter from the drawer override this.
      if (r.status === 'cancelled' && !showCancelled && statusFilter !== 'cancelled') {
        return false;
      }

      // Quick filter
      if (quickFilter === 'mine') {
        if (myPlayerId == null) return false;
        if (!r.joined_players.some((p) => p.player_id === myPlayerId)) return false;
      } else if (quickFilter === 'has_joined') {
        if (r.joined_players.length === 0) return false;
      } else if (quickFilter === 'major') {
        if (!r.assigned_tournament) return false;
      }

      // Detailed filters from the drawer
      if (statusFilter && r.status !== statusFilter) return false;
      if (courseFilter) {
        const c = r.course_name || r.complex_name;
        if (c !== courseFilter) return false;
      }
      const rDate = dateKey(r.reservation_date);
      if (dateFrom && (!rDate || rDate < dateFrom)) return false;
      if (dateTo && (!rDate || rDate > dateTo)) return false;
      if (bookedByFilter && String(r.booked_by_player_id ?? '') !== bookedByFilter) return false;
      if (joinedPlayerFilter) {
        const pid = Number(joinedPlayerFilter);
        if (!r.joined_players.some((p) => p.player_id === pid)) return false;
      }
      if (hasJoinedFilter && r.joined_players.length === 0) return false;
      return true;
    });
  }, [reservations, quickFilter, showCancelled, myPlayerId, statusFilter, courseFilter, dateFrom, dateTo, bookedByFilter, joinedPlayerFilter, hasJoinedFilter]);

  // Reservations in the visible week (sorted by time)
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const weekReservations = useMemo(() => {
    const startStr = formatLocalDate(weekStart);
    const endStr = formatLocalDate(weekEnd);
    return filteredAll
      .filter((r) => {
        const k = dateKey(r.reservation_date);
        return !!k && k >= startStr && k <= endStr;
      })
      .sort((a, b) => {
        const da = (dateKey(a.reservation_date) ?? '').localeCompare(dateKey(b.reservation_date) ?? '');
        if (da !== 0) return da;
        return parseTeeTimeMinutes(a.tee_time) - parseTeeTimeMinutes(b.tee_time);
      });
  }, [filteredAll, weekStart, weekEnd]);

  // Group by date
  const byDate = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    weekReservations.forEach((r) => {
      const key = dateKey(r.reservation_date) ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return map;
  }, [weekReservations]);

  // For day-strip indicators we want counts using *all* visible filters
  const dayMeta = useMemo(() => {
    return weekDays.map((d) => {
      const key = formatLocalDate(d);
      const list = byDate.get(key) ?? [];
      const hasMine = myPlayerId != null && list.some((r) => r.joined_players.some((p) => p.player_id === myPlayerId));
      return { date: d, key, count: list.length, hasMine };
    });
  }, [weekDays, byDate, myPlayerId]);

  const anyDetailedFilter =
    !!statusFilter || !!courseFilter || !!dateFrom || !!dateTo ||
    !!bookedByFilter || !!joinedPlayerFilter || hasJoinedFilter;

  function clearDetailedFilters() {
    setStatusFilter('');
    setCourseFilter('');
    setDateFrom('');
    setDateTo('');
    setBookedByFilter('');
    setJoinedPlayerFilter('');
    setHasJoinedFilter(false);
  }

  const [live, setLive] = useState(false);
  const [changelogTicks, setChangelogTicks] = useState<Record<number, number>>({});

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;

    async function connect() {
      if (cancelled) return;
      try {
        const t = await token();
        if (cancelled) return;
        es = new EventSource(`${APIURL}/reservations/events?access_token=${encodeURIComponent(t)}`);

        es.onopen = () => setLive(true);

        es.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'hello') return;
            if (refetchTimer) clearTimeout(refetchTimer);
            refetchTimer = setTimeout(() => fetchReservationsRef.current(), 400);
            if (data.reservation_id) {
              setChangelogTicks((prev) => ({
                ...prev,
                [data.reservation_id]: (prev[data.reservation_id] ?? 0) + 1,
              }));
            }
          } catch {
            /* heartbeat — ignore */
          }
        };

        es.onerror = () => {
          setLive(false);
          es?.close();
          es = null;
          if (!cancelled) reconnectTimer = setTimeout(connect, 3000);
        };
      } catch {
        if (!cancelled) reconnectTimer = setTimeout(connect, 5000);
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (refetchTimer) clearTimeout(refetchTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [getAccessTokenSilently]);

  async function triggerPoll() {
    setPolling(true);
    setPollMsg('');
    try {
      const t = await token();
      const res = await fetch(`${APIURL}/email/poll`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) throw new Error('Poll request failed');
      await fetchReservations();
      setPollMsg('Inbox checked.');
    } catch {
      setPollMsg('Failed to check inbox — is the backend running?');
    } finally {
      setPolling(false);
    }
  }

  function openManualModal() {
    setManualForm(emptyManualForm());
    setManualError(null);
    setShowManualModal(true);
  }

  async function submitManualReservation() {
    if (!manualForm.course_id || !manualForm.reservation_date) {
      setManualError('Please select a course and date.');
      return;
    }
    const teeTime = `${manualForm.tee_hour}:${manualForm.tee_minute} ${manualForm.tee_period}`;
    setManualSaving(true);
    setManualError(null);
    try {
      const t = await token();
      const res = await fetch(`${APIURL}/reservations/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({
          course_name: manualForm.course_name.trim() || null,
          complex_name: manualForm.complex_name.trim() || null,
          facility_id: manualForm.facility_id ? Number(manualForm.facility_id) : null,
          course_id: manualForm.course_id ? Number(manualForm.course_id) : null,
          reservation_date: manualForm.reservation_date,
          tee_time: teeTime,
          num_players: manualForm.num_players ? Number(manualForm.num_players) : null,
          num_holes: manualForm.num_holes ? Number(manualForm.num_holes) : null,
          reservation_name: manualForm.reservation_name.trim() || null,
          booking_ref: manualForm.booking_ref.trim() || null,
          ai_notes: manualForm.ai_notes.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create reservation.');
      }
      await fetchReservations();
      setShowManualModal(false);
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to create reservation.');
    } finally {
      setManualSaving(false);
    }
  }

  async function joinReservation(id: number) {
    const t = await token();
    const res = await fetch(`${APIURL}/reservations/${id}/join`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to join');
      return;
    }
    await fetchReservations();
  }

  async function addPlayer(id: number, playerId: number) {
    const t = await token();
    const res = await fetch(`${APIURL}/reservations/${id}/players`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ player_id: playerId }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Failed to add player');
      return;
    }
    await fetchReservations();
  }

  async function removePlayer(reservationId: number, playerId: number) {
    const t = await token();
    const res = await fetch(`${APIURL}/reservations/${reservationId}/players/${playerId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) return alert('Failed to remove player');
    await fetchReservations();
  }

  async function changeStatus(id: number, status: 'active' | 'cancelled') {
    const t = await token();
    const res = await fetch(`${APIURL}/reservations/${id}/status`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) return alert('Failed to update status');
    await fetchReservations();
  }

  async function deleteReservation(id: number) {
    if (!window.confirm('Delete this manual reservation? This cannot be undone.')) return;
    const t = await token();
    const res = await fetch(`${APIURL}/reservations/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return alert(data.error || 'Failed to delete reservation.');
    }
    await fetchReservations();
  }

  function goPrevWeek() { setWeekStart((w) => addDays(w, -7)); }
  function goNextWeek() { setWeekStart((w) => addDays(w, 7)); }
  function goThisWeek() { setWeekStart(startOfWeek(new Date())); }

  const isCurrentWeek = sameDay(weekStart, startOfWeek(today));
  const weekLabel = isCurrentWeek
    ? 'This Week'
    : sameDay(weekStart, startOfWeek(addDays(today, 7)))
      ? 'Next Week'
      : sameDay(weekStart, startOfWeek(addDays(today, -7)))
        ? 'Last Week'
        : 'Week of';

  return (
    <div style={pageWrap}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .ssa-day-pill:hover { background: #eef3f9; }
        .ssa-icon-btn:hover { background: #e7ecf3; }
        .ssa-card:active { transform: scale(0.998); }
        .ssa-scroll-x { -webkit-overflow-scrolling: touch; }
        .ssa-scroll-x::-webkit-scrollbar { display: none; }
        @media (max-width: 480px) {
          .ssa-page { padding: 0 !important; }
          .ssa-container { border-radius: 0 !important; box-shadow: none !important; border-left: 0 !important; border-right: 0 !important; }
        }
      `}</style>

      <div className="ssa-page" style={{ padding: '12px', maxWidth: '720px', margin: '0 auto' }}>
        <div className="ssa-container" style={containerStyle}>

          {/* App header */}
          <div style={headerStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Reservations</h2>
              <span
                title={live ? 'Live updates connected' : 'Reconnecting…'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 11, color: live ? '#27ae60' : '#aaa',
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: live ? '#27ae60' : '#bbb' }} />
                {live ? 'Live' : 'Offline'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {pollMsg && (
                <span style={{ fontSize: 11, color: pollMsg.startsWith('Failed') ? '#c0392b' : '#27ae60' }}>
                  {pollMsg}
                </span>
              )}
              <button
                className="ssa-icon-btn"
                onClick={() => setShowFilters((v) => !v)}
                style={{ ...iconBtn, position: 'relative' }}
                title="Filters"
                aria-label="Toggle filters"
              >
                <FunnelIcon />
                {anyDetailedFilter && <span style={filterBadge}>{[statusFilter, courseFilter, dateFrom, dateTo, bookedByFilter, joinedPlayerFilter, hasJoinedFilter ? 'has' : ''].filter(Boolean).length}</span>}
              </button>
              <button
                className="ssa-icon-btn"
                onClick={openManualModal}
                style={{ ...iconBtn, fontSize: 18, fontWeight: 700, color: '#2980b9' }}
                title="Add manual reservation"
                aria-label="Add manual reservation"
              >
                +
              </button>
              <button
                className="ssa-icon-btn"
                onClick={triggerPoll}
                disabled={polling}
                style={{ ...iconBtn, cursor: polling ? 'not-allowed' : 'pointer' }}
                title="Refresh inbox"
                aria-label="Refresh"
              >
                <RefreshIcon spinning={polling} />
              </button>
            </div>
          </div>

          {/* Week navigator */}
          <div style={weekNavStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <button className="ssa-icon-btn" onClick={goPrevWeek} style={weekArrow} aria-label="Previous week">‹</button>
              <div style={{ textAlign: 'center', flex: 1, cursor: isCurrentWeek ? 'default' : 'pointer' }} onClick={isCurrentWeek ? undefined : goThisWeek}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{weekLabel}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{formatWeekRange(weekStart)}</div>
              </div>
              <button className="ssa-icon-btn" onClick={goNextWeek} style={weekArrow} aria-label="Next week">›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {dayMeta.map((dm, i) => {
                const isToday = sameDay(dm.date, today);
                return (
                  <button
                    key={dm.key}
                    className="ssa-day-pill"
                    onClick={() => {
                      const el = document.getElementById(`day-${dm.key}`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    style={{
                      ...dayPill,
                      background: isToday ? '#ecf6ff' : (dm.count === 0 ? '#f7f9fc' : '#f7f9fc'),
                      borderColor: isToday ? '#b8dcf4' : 'transparent',
                      boxShadow: dm.hasMine ? 'inset 0 -3px 0 #27ae60' : undefined,
                      cursor: dm.count > 0 ? 'pointer' : 'default',
                    }}
                    aria-label={`${WEEKDAY_SHORT[i]} ${dm.date.getDate()}, ${dm.count} tee times`}
                  >
                    <div style={{ fontSize: 10, color: '#6b7280', letterSpacing: 0.5, textTransform: 'uppercase' }}>{WEEKDAY_SHORT[i]}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isToday ? '#1f6391' : '#1f2937', marginTop: 2 }}>{dm.date.getDate()}</div>
                    <div style={{ fontSize: 9, color: dm.count > 0 ? '#2980b9' : '#9ca3af', marginTop: 3, minHeight: 11 }}>
                      {dm.count > 0 ? dm.count : '—'}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick filter pills */}
          <div className="ssa-scroll-x" style={pillRow}>
            {([
              { key: 'all', label: 'All' },
              { key: 'mine', label: 'Mine', disabled: myPlayerId == null },
              { key: 'has_joined', label: 'Has joined' },
              { key: 'major', label: '🏆 Major' },
            ] as const).map((p) => (
              <button
                key={p.key}
                disabled={'disabled' in p && (p as { disabled?: boolean }).disabled}
                onClick={() => setQuickFilter(p.key)}
                style={{
                  ...pillStyle,
                  background: quickFilter === p.key ? '#2980b9' : '#fff',
                  color: quickFilter === p.key ? '#fff' : '#4a5568',
                  borderColor: quickFilter === p.key ? '#2980b9' : '#e3e8ef',
                  opacity: ('disabled' in p && (p as { disabled?: boolean }).disabled) ? 0.4 : 1,
                  cursor: ('disabled' in p && (p as { disabled?: boolean }).disabled) ? 'not-allowed' : 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={() => setShowCancelled((v) => !v)}
              aria-pressed={showCancelled}
              title={showCancelled ? 'Hide cancelled reservations' : 'Show cancelled reservations'}
              style={{
                ...pillStyle,
                background: showCancelled ? '#c0392b' : '#fff',
                color: showCancelled ? '#fff' : '#4a5568',
                borderColor: showCancelled ? '#c0392b' : '#e3e8ef',
                cursor: 'pointer',
              }}
            >
              {showCancelled ? '✓ Show cancelled' : 'Show cancelled'}
            </button>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af', alignSelf: 'center', paddingRight: 4 }}>
              {weekReservations.length} this week
            </span>
          </div>

          {/* Detailed filter panel (collapsible) */}
          {showFilters && (
            <div style={filterPanel}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={filterInput}>
                  <option value="">Status: All</option>
                  <option value="active">Active</option>
                  <option value="manual">Manual</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} style={filterInput}>
                  <option value="">Course: All</option>
                  {courseOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={bookedByFilter} onChange={(e) => setBookedByFilter(e.target.value)} style={filterInput}>
                  <option value="">Booked by: Anyone</option>
                  {bookerOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
                <select value={joinedPlayerFilter} onChange={(e) => setJoinedPlayerFilter(e.target.value)} style={filterInput}>
                  <option value="">Player joined: Anyone</option>
                  {allPlayers.map((p) => <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                <label style={dateFilterLabel}>
                  From
                  <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={filterInput} />
                </label>
                <label style={dateFilterLabel}>
                  To
                  <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={filterInput} />
                </label>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4a5568', marginTop: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={hasJoinedFilter} onChange={(e) => setHasJoinedFilter(e.target.checked)} />
                Only show reservations with joined players
              </label>
              {(dateFrom || dateTo) && (
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '6px 0 0' }}>
                  Date range filters apply within the visible week. Use the week navigator to scope across weeks.
                </p>
              )}
              {anyDetailedFilter && (
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <button onClick={clearDetailedFilters} style={ghostBtn}>Clear filters</button>
                </div>
              )}
            </div>
          )}

          {/* Manual reservation modal */}
          {showManualModal && (
            <ManualReservationModal
              form={manualForm}
              setForm={setManualForm}
              saving={manualSaving}
              error={manualError}
              onSubmit={submitManualReservation}
              onClose={() => !manualSaving && setShowManualModal(false)}
              getToken={token}
              apiUrl={APIURL}
            />
          )}

          {/* Feed */}
          <div style={{ padding: '6px 0 20px' }}>
            {loading ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>Loading reservations…</p>
            ) : weekReservations.length === 0 && reservations.length === 0 ? (
              <div style={emptyState}>
                <p style={{ fontSize: 15 }}>No reservations found.</p>
                <p style={{ fontSize: 12, color: '#9ca3af' }}>Try refreshing the inbox.</p>
              </div>
            ) : (
              <>
                {dayMeta.map((dm) => {
                  const list = byDate.get(dm.key) ?? [];
                  const isToday = sameDay(dm.date, today);
                  return (
                    <div key={dm.key} id={`day-${dm.key}`}>
                      <div style={{ ...dayHeader, color: isToday ? '#1f6391' : '#2d3748' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {isToday ? 'Today · ' : ''}
                          {dm.date.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>
                          {list.length === 0 ? '—' : `${list.length} tee time${list.length === 1 ? '' : 's'}`}
                        </span>
                      </div>
                      {list.length === 0 ? (
                        <div style={emptyDay}>No tee times this day</div>
                      ) : (
                        list.map((r) => (
                          <ReservationCard
                            key={r.reservation_id}
                            reservation={r}
                            allPlayers={allPlayers}
                            myPlayerId={myPlayerId}
                            getToken={token}
                            changelogTick={changelogTicks[r.reservation_id] ?? 0}
                            expanded={expandedId === r.reservation_id}
                            onToggle={() => setExpandedId((id) => id === r.reservation_id ? null : r.reservation_id)}
                            onJoin={() => joinReservation(r.reservation_id)}
                            onAddPlayer={(pid) => addPlayer(r.reservation_id, pid)}
                            onRemovePlayer={(pid) => removePlayer(r.reservation_id, pid)}
                            onStatusChange={(s) => changeStatus(r.reservation_id, s)}
                            onDelete={() => deleteReservation(r.reservation_id)}
                          />
                        ))
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Jump-to-today FAB (only when not on current week) */}
          {!isCurrentWeek && (
            <button onClick={goThisWeek} style={fabStyle} aria-label="Jump to this week" title="Jump to this week">
              ⌖
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReservationCard({
  reservation: r,
  allPlayers,
  myPlayerId,
  getToken,
  changelogTick,
  expanded,
  onToggle,
  onJoin,
  onAddPlayer,
  onRemovePlayer,
  onStatusChange,
  onDelete,
}: {
  reservation: Reservation;
  allPlayers: PlayerOption[];
  myPlayerId: number | null;
  getToken: () => Promise<string>;
  changelogTick: number;
  expanded: boolean;
  onToggle: () => void;
  onJoin: () => void;
  onAddPlayer: (playerId: number) => void;
  onRemovePlayer: (playerId: number) => void;
  onStatusChange: (status: 'active' | 'cancelled') => void;
  onDelete: () => void;
}) {
  const [pickerId, setPickerId] = useState<string>('');
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelog, setChangelog] = useState<ChangelogEntry[] | null>(null);
  const [changelogLoading, setChangelogLoading] = useState(false);

  const showChangelogRef = useRef(showChangelog);
  useEffect(() => { showChangelogRef.current = showChangelog; }, [showChangelog]);

  // Live refresh of the changelog when an SSE tick arrives and the panel is open
  useEffect(() => {
    if (changelogTick === 0 || !showChangelogRef.current) return;
    getToken()
      .then((t) =>
        fetch(`${APIURL}/reservations/${r.reservation_id}/changelog`, {
          headers: { Authorization: `Bearer ${t}` },
        })
      )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) setChangelog(data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changelogTick]);

  async function loadChangelog() {
    if (changelog !== null) { setShowChangelog((v) => !v); return; }
    setChangelogLoading(true);
    try {
      const t = await getToken();
      const res = await fetch(`${APIURL}/reservations/${r.reservation_id}/changelog`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) setChangelog(await res.json());
    } finally {
      setChangelogLoading(false);
      setShowChangelog(true);
    }
  }

  const joined = r.joined_players || [];
  const seats = r.num_players ?? 0;
  const full = seats > 0 && joined.length >= seats;
  const userJoined = myPlayerId != null && joined.some((p) => p.player_id === myPlayerId);
  const isCancelled = r.status === 'cancelled';

  const joinedIds = new Set(joined.map((p) => p.player_id));
  const availablePlayers = allPlayers.filter((p) => !joinedIds.has(p.player_id));

  const { time, ampm } = splitTime(r.tee_time);
  const seatSlots = seats > 0 ? seats : Math.max(joined.length, 4);
  const emptySlots = Math.max(0, seatSlots - joined.length);

  return (
    <div
      className="ssa-card"
      style={{
        ...cardStyle,
        opacity: isCancelled ? 0.65 : 1,
        borderColor: expanded ? '#b8dcf4' : '#e3e8ef',
        boxShadow: expanded ? '0 2px 12px rgba(41,128,185,0.15)' : '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {/* Compact row (clickable to expand) */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        aria-expanded={expanded}
        style={{ cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {/* Time block */}
          <div style={{ flexShrink: 0, textAlign: 'center', minWidth: 60, borderRight: '1px solid #e3e8ef', paddingRight: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.1 }}>{time}</div>
            {ampm && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 0 }}>{ampm}</div>}
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
              {r.num_holes ? `${r.num_holes} holes` : ''}
            </div>
          </div>

          {/* Main info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.course_name || r.complex_name || 'Unknown course'}
                </div>
                {r.complex_name && r.course_name && r.complex_name !== r.course_name && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{r.complex_name}</div>
                )}
                {r.assigned_tournament && (
                  <div
                    title={`Assigned to ${r.assigned_tournament.tournament_name} — shown as a major round, not a regular tee time.`}
                    style={{
                      display: 'inline-block', marginTop: 4,
                      background: '#0d6efd1a', color: '#0d6efd',
                      border: '1px solid #0d6efd44', borderRadius: 10,
                      padding: '1px 8px', fontSize: 10, fontWeight: 700,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
                    }}
                  >
                    🏆 {r.assigned_tournament.tournament_name}
                  </div>
                )}
              </div>
              <span
                style={{
                  background: STATUS_COLORS[r.status] + '22',
                  color: STATUS_COLORS[r.status],
                  border: `1px solid ${STATUS_COLORS[r.status]}44`,
                  borderRadius: 10,
                  padding: '2px 7px',
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {STATUS_LABELS[r.status]}
              </span>
            </div>
            {(r.booked_by_first_name || r.booked_by_alias) && (
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Booked by{' '}
                {r.booked_by_first_name ? (
                  <strong style={{ color: '#374151', fontWeight: 600 }}>
                    {r.booked_by_first_name} {r.booked_by_last_name ?? ''}
                  </strong>
                ) : (
                  <em>unknown</em>
                )}
                {r.booking_ref && <> · Ref <code style={{ fontSize: 10 }}>{r.booking_ref}</code></>}
              </div>
            )}
          </div>
        </div>

        {/* Players row — avatars + count only (no buttons here, to keep the action footer predictable) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex' }}>
            {joined.map((p, idx) => (
              <span
                key={p.player_id}
                title={`${p.first_name} ${p.last_name}`}
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: p.player_id === myPlayerId ? '#27ae60' : avatarColor(p.player_id),
                  color: '#fff', fontSize: 10, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid #fff', marginLeft: idx === 0 ? 0 : -7,
                }}
              >
                {initials(p.first_name, p.last_name)}
              </span>
            ))}
            {Array.from({ length: Math.min(emptySlots, 4) }).map((_, i) => (
              <span
                key={`empty-${i}`}
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: '#fff', border: '2px dashed #cbd5e1', color: '#cbd5e1',
                  fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  marginLeft: joined.length === 0 && i === 0 ? 0 : -7,
                }}
              >+</span>
            ))}
          </div>
          <span style={{ fontSize: 11, color: full ? '#c0392b' : '#6b7280', fontWeight: full ? 600 : 400, marginLeft: 6 }}>
            {joined.length}{seats ? ` of ${seats}` : ''}{full ? ' — Full' : ''}
          </span>
        </div>

        {/* Action footer — Details left, primary action pinned right */}
        <div style={cardFooterStyle}>
          <span style={detailsAffordanceStyle}>
            {expanded ? 'Hide' : 'Details'}
            <span style={{ display: 'inline-block', transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 150ms' }}>▾</span>
          </span>
          {!isCancelled && !userJoined && myPlayerId != null && !full && (
            <button
              onClick={(e) => { e.stopPropagation(); onJoin(); }}
              style={primaryActionBtn}
            >
              Join
            </button>
          )}
          {!isCancelled && userJoined && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemovePlayer(myPlayerId!); }}
              style={secondaryActionBtn}
            >
              Leave
            </button>
          )}
          {!isCancelled && !userJoined && full && (
            <span style={fullBadge}>Full</span>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eef2f7' }}>
          {/* Meta block */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#374151' }}>
            {r.reservation_date && (
              <div>
                <span style={metaLabel}>Date</span>{' '}
                {parseLocalDate(r.reservation_date)?.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {r.tee_time && ` · ${r.tee_time}`}
              </div>
            )}
            {r.course_address && (
              <div>
                <span style={metaLabel}>Address</span>{' '}
                <span style={{ color: '#4a5568' }}>{r.course_address}</span>
              </div>
            )}
            {r.reservation_name && (
              <div>
                <span style={metaLabel}>Under</span> <strong>{r.reservation_name}</strong>
              </div>
            )}
            {r.booking_ref && (
              <div>
                <span style={metaLabel}>Booking ref</span>{' '}
                <code style={{ fontSize: 12 }}>{r.booking_ref}</code>
              </div>
            )}
            {r.booked_by_alias && (
              <div>
                <span style={metaLabel}>Alias</span>{' '}
                <code style={{ fontSize: 12 }}>{r.booked_by_alias}</code>
              </div>
            )}
            {r.ai_notes && (
              <div style={{ marginTop: 4, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#78350f', fontSize: 12 }}>
                {r.ai_notes}
              </div>
            )}
          </div>

          {/* Player management */}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eef2f7' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 13, color: '#374151' }}>
                Players ({joined.length}{seats ? ` of ${seats}` : ''})
              </strong>
            </div>

            {joined.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {joined.map((p) => (
                  <span key={p.player_id} style={playerChip}>
                    {p.first_name} {p.last_name}
                    {!isCancelled && (
                      <button
                        onClick={() => onRemovePlayer(p.player_id)}
                        style={chipRemoveBtn}
                        title="Remove from reservation"
                        aria-label={`Remove ${p.first_name} ${p.last_name}`}
                      >×</button>
                    )}
                  </span>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 8px 0' }}>No players yet</p>
            )}

            {!isCancelled && !full && availablePlayers.length > 0 && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <select
                  value={pickerId}
                  onChange={(e) => setPickerId(e.target.value)}
                  style={{ padding: '5px 8px', fontSize: 13, borderRadius: 4, border: '1px solid #ccc', flex: 1 }}
                >
                  <option value="">Add player…</option>
                  {availablePlayers.map((p) => (
                    <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
                <button
                  onClick={() => { if (pickerId) { onAddPlayer(Number(pickerId)); setPickerId(''); } }}
                  disabled={!pickerId}
                  style={{ ...actionBtn('#2980b9'), opacity: pickerId ? 1 : 0.5 }}
                >
                  Add
                </button>
              </div>
            )}
            {full && !isCancelled && (
              <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0 0' }}>Reservation is full</p>
            )}
          </div>

          {/* Footer actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center', paddingTop: 12, borderTop: '1px solid #eef2f7' }}>
            {r.email_count > 0 && (
              <button
                onClick={loadChangelog}
                disabled={changelogLoading}
                style={{ ...actionBtn('#555'), background: 'transparent', color: '#555', border: '1px solid #ccc' }}
              >
                {changelogLoading
                  ? 'Loading…'
                  : showChangelog
                    ? 'Hide Email History'
                    : `Email History (${r.email_count})`}
              </button>
            )}
            {r.updated_at && (
              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>
                Updated {formatEst(r.updated_at)}
              </span>
            )}
            {isCancelled ? (
              <button onClick={() => onStatusChange('active')} style={actionBtn('#27ae60')}>
                Restore
              </button>
            ) : (
              <button onClick={() => onStatusChange('cancelled')} style={actionBtn('#c0392b')}>
                Cancel
              </button>
            )}
            {r.status === 'manual' && (
              <button onClick={onDelete} style={actionBtn('#7f1d1d')} title="Permanently delete this manual reservation">
                Delete
              </button>
            )}
          </div>

          {/* Changelog */}
          {showChangelog && changelog && (
            <div style={{ marginTop: 12, borderTop: '1px solid #eef2f7', paddingTop: 12 }}>
              <strong style={{ fontSize: 13, color: '#374151' }}>Email History</strong>
              {' '}<span style={{ fontSize: 11, color: '#9ca3af' }}>(times shown in EST)</span>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {changelog.map((entry) => (
                  <ChangelogRow key={entry.email_id} entry={entry} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Manual reservation modal ────────────────────────────────────────────────

const HOURS = ['6','7','8','9','10','11','12','1','2','3','4','5'];
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

interface CourseOption { course_id: number; course_name: string; facility_id?: number | null; facility_name?: string | null; }

interface ManualReservationModalProps {
  form: {
    course_name: string; complex_name: string;
    facility_id: string; course_id: string;
    reservation_date: string;
    tee_hour: string; tee_minute: string; tee_period: 'AM' | 'PM';
    num_players: string; num_holes: string;
    reservation_name: string; booking_ref: string; ai_notes: string;
  };
  setForm: React.Dispatch<React.SetStateAction<any>>;
  saving: boolean;
  error: string | null;
  onSubmit: () => void;
  onClose: () => void;
  getToken: () => Promise<string>;
  apiUrl?: string;
}

function ManualReservationModal({ form, setForm, saving, error, onSubmit, onClose, getToken, apiUrl }: ManualReservationModalProps) {
  const set = (key: string, val: string) => setForm((f: any) => ({ ...f, [key]: val }));

  const [courses, setCourses] = React.useState<CourseOption[]>([]);

  // Load existing courses (grouped by facility) so the user picks a canonical
  // row instead of creating duplicates.
  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken();
        const headers = { Authorization: `Bearer ${t}` };
        const cRes = await fetch(`${apiUrl}/coursesv2`, { headers }).then((r) => r.json()).catch(() => []);
        setCourses(Array.isArray(cRes) ? cRes : []);
      } catch { /* non-fatal */ }
    })();
  }, [getToken, apiUrl]);

  // Selecting a course links the canonical course row and back-fills its facility.
  const onCourseSelect = (cId: string) => {
    const c = courses.find((cc) => String(cc.course_id) === cId);
    setForm((f: any) => ({
      ...f,
      course_id: cId,
      course_name: c ? c.course_name : '',
      facility_id: c && c.facility_id != null ? String(c.facility_id) : f.facility_id,
      complex_name: c && c.facility_name ? c.facility_name : f.complex_name,
    }));
  };

  const fieldLabel: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4,
  };
  const inp: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box' as const,
    padding: '7px 10px', fontSize: 13, border: '1px solid #d1d5db',
    borderRadius: 6, background: '#fff', color: '#111827',
  };
  const section: React.CSSProperties = {
    borderTop: '1px solid #f0f0f0', paddingTop: 14, marginTop: 14,
  };
  const sectionTitle: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10,
  };
  const grid2: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
  };
  const matchHint: React.CSSProperties = { display: 'block', fontSize: 11, color: '#157347', marginTop: 3 };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        zIndex: 1000, padding: '16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: 22,
          width: '100%', maxWidth: 500, marginTop: 24, marginBottom: 24,
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Add reservation</h3>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280' }}>
              Will be tagged <span style={{ fontWeight: 700, color: '#8e44ad' }}>Manual</span>
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>

        {/* ── Section: Course ── */}
        <div style={sectionTitle}>Course</div>
        <div>
          <span style={fieldLabel}>Course *</span>
          <select style={inp} value={form.course_id} onChange={(e) => onCourseSelect(e.target.value)}>
            <option value="">Select a course…</option>
            {Object.entries(
              courses.reduce((groups: Record<string, CourseOption[]>, c) => {
                const key = c.facility_name || 'Other';
                (groups[key] = groups[key] || []).push(c);
                return groups;
              }, {})
            ).map(([facility, items]) => (
              <optgroup key={facility} label={facility}>
                {items.map((c) => (
                  <option key={c.course_id} value={c.course_id}>{c.course_name}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {form.course_id && <span style={matchHint}>✓ Linked to {form.complex_name || 'course'}</span>}
        </div>

        {/* ── Section: Date & Time ── */}
        <div style={section}>
          <div style={sectionTitle}>Date &amp; Time</div>
          <div style={grid2}>
            <div>
              <span style={fieldLabel}>Date *</span>
              <input style={inp} type="date" value={form.reservation_date}
                onChange={(e) => set('reservation_date', e.target.value)} />
            </div>
            <div>
              <span style={fieldLabel}>Tee time</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {/* Hour */}
                <select
                  value={form.tee_hour}
                  onChange={(e) => set('tee_hour', e.target.value)}
                  style={{ ...inp, flex: '0 0 56px', padding: '7px 4px', textAlign: 'center' }}
                >
                  {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                {/* Minute */}
                <select
                  value={form.tee_minute}
                  onChange={(e) => set('tee_minute', e.target.value)}
                  style={{ ...inp, flex: '0 0 56px', padding: '7px 4px', textAlign: 'center' }}
                >
                  {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                {/* AM / PM */}
                <div style={{ display: 'flex', gap: 0, border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden' }}>
                  {(['AM', 'PM'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => set('tee_period', p)}
                      style={{
                        padding: '7px 10px', fontSize: 12, fontWeight: 600, border: 'none',
                        background: form.tee_period === p ? '#2980b9' : '#fff',
                        color: form.tee_period === p ? '#fff' : '#374151',
                        cursor: 'pointer',
                      }}
                    >{p}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section: Booking details ── */}
        <div style={section}>
          <div style={sectionTitle}>Booking details</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={grid2}>
              <div>
                <span style={fieldLabel}>Players</span>
                <select style={inp} value={form.num_players} onChange={(e) => set('num_players', e.target.value)}>
                  {['1','2','3','4','5','6','7','8'].map((n) => (
                    <option key={n} value={n}>{n} player{n !== '1' ? 's' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <span style={fieldLabel}>Holes</span>
                <select style={inp} value={form.num_holes} onChange={(e) => set('num_holes', e.target.value)}>
                  <option value="">— not set —</option>
                  <option value="9">9 holes</option>
                  <option value="18">18 holes</option>
                </select>
              </div>
            </div>
            <div>
              <span style={fieldLabel}>Name on reservation</span>
              <input style={inp} type="text" value={form.reservation_name}
                onChange={(e) => set('reservation_name', e.target.value)}
                placeholder="e.g. T. Wong" />
            </div>
            <div>
              <span style={fieldLabel}>Booking reference</span>
              <input style={inp} type="text" value={form.booking_ref}
                onChange={(e) => set('booking_ref', e.target.value)}
                placeholder="e.g. BK-12345" />
            </div>
          </div>
        </div>

        {/* ── Section: Notes ── */}
        <div style={section}>
          <div style={sectionTitle}>Notes</div>
          <textarea
            style={{ ...inp, resize: 'vertical', minHeight: 60 }}
            value={form.ai_notes}
            onChange={(e) => set('ai_notes', e.target.value)}
            placeholder="Any notes about this reservation…"
            rows={3}
          />
        </div>

        {error && (
          <div style={{ marginTop: 12, color: '#c0392b', fontSize: 12, padding: '8px 10px', background: '#fff5f5', borderRadius: 6 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onClose} disabled={saving} style={ghostBtn}>Cancel</button>
          <button
            onClick={onSubmit}
            disabled={saving}
            style={{
              background: saving ? '#93c5fd' : '#2980b9', color: '#fff', border: 'none',
              borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 150ms',
            }}
          >
            {saving ? 'Saving…' : 'Create reservation'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ACTION_STYLES: Record<string, { dot: string; label: string }> = {
  initial:      { dot: '#27ae60', label: 'Booking created' },
  update:       { dot: '#f39c12', label: 'Updated' },
  cancellation: { dot: '#c0392b', label: 'Cancelled' },
  no_change:    { dot: '#bdc3c7', label: 'No changes' },
};

function ChangelogRow({ entry }: { entry: ChangelogEntry }) {
  const style = ACTION_STYLES[entry.action] ?? ACTION_STYLES.no_change;
  const [showEmail, setShowEmail] = useState(false);
  const hasEmailContent = !!(entry.raw_html || entry.raw_body);

  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: style.dot, marginTop: 3, flexShrink: 0 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: style.dot }}>{style.label}</span>
          <span style={{ color: '#9ca3af', fontSize: 11 }}>{formatEst(entry.received_at)}</span>
        </div>
        {entry.raw_subject && (
          <div style={{ color: '#6b7280', fontSize: 12, marginTop: 1 }}>
            {entry.raw_from && <span>{entry.raw_from} · </span>}
            <em>{entry.raw_subject}</em>
          </div>
        )}
        {entry.changes.length > 0 && (
          <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {entry.changes.map((c) => (
              <div key={c.field} style={{ fontSize: 12, color: '#4a5568' }}>
                <span style={{ fontWeight: 500 }}>{c.field_label}:</span>{' '}
                {c.old_value != null
                  ? <><span style={{ textDecoration: 'line-through', color: '#e74c3c' }}>{c.old_value}</span>{' → '}</>
                  : null}
                <span style={{ color: '#27ae60' }}>{c.new_value ?? '—'}</span>
              </div>
            ))}
          </div>
        )}
        {hasEmailContent && (
          <div style={{ marginTop: 6 }}>
            <button
              onClick={() => setShowEmail((v) => !v)}
              style={{
                background: 'transparent',
                border: '1px solid #ddd',
                color: '#555',
                fontSize: 11,
                padding: '2px 8px',
                borderRadius: 3,
                cursor: 'pointer',
              }}
            >
              {showEmail ? 'Hide Email' : 'View Email'}
            </button>
            {showEmail && (
              <div
                style={{
                  marginTop: 6,
                  border: '1px solid #e0e0e0',
                  borderRadius: 4,
                  background: '#fff',
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '8px 12px', background: '#f8f8f8', borderBottom: '1px solid #e0e0e0', fontSize: 12, color: '#444' }}>
                  <div><strong>From:</strong> {entry.raw_from || '—'}</div>
                  <div><strong>Subject:</strong> {entry.raw_subject || '—'}</div>
                </div>
                {entry.raw_html ? (
                  <iframe
                    title={`email-${entry.email_id}`}
                    srcDoc={entry.raw_html}
                    sandbox=""
                    style={{
                      width: '100%',
                      minHeight: 400,
                      border: 'none',
                      background: '#fff',
                      display: 'block',
                    }}
                  />
                ) : (
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      color: '#4a5568',
                      padding: 10,
                    }}
                  >
                    {entry.raw_body}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatEst(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const formatted = d.toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${formatted} EST`;
}

function FunnelIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function RefreshIcon({ spinning }: { spinning?: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: spinning ? 'spin 1s linear infinite' : undefined }}
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

/* Styles */
const pageWrap: React.CSSProperties = {
  background: '#f4f6f9',
  minHeight: '100vh',
};

const containerStyle: React.CSSProperties = {
  background: '#f4f6f9',
  borderRadius: 12,
  border: '1px solid #e3e8ef',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  overflow: 'hidden',
  position: 'relative',
};

const headerStyle: React.CSSProperties = {
  background: '#fff',
  borderBottom: '1px solid #e3e8ef',
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'sticky',
  top: 0,
  zIndex: 4,
};

const iconBtn: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: '#f1f4f9',
  border: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#4a5568',
  cursor: 'pointer',
};

const filterBadge: React.CSSProperties = {
  position: 'absolute',
  top: -2,
  right: -2,
  background: '#2980b9',
  color: '#fff',
  borderRadius: 8,
  fontSize: 9,
  padding: '1px 5px',
  fontWeight: 700,
  border: '2px solid #fff',
};

const weekNavStyle: React.CSSProperties = {
  background: '#fff',
  padding: '10px 14px 12px',
  borderBottom: '1px solid #e3e8ef',
  position: 'sticky',
  top: 58,
  zIndex: 3,
};

const weekArrow: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: '1px solid #e3e8ef',
  background: '#fff',
  fontSize: 16,
  color: '#4a5568',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};

const dayPill: React.CSSProperties = {
  textAlign: 'center',
  padding: '6px 0 8px',
  borderRadius: 8,
  border: '1px solid transparent',
  background: '#f7f9fc',
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
};

const pillRow: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  padding: '10px 14px',
  background: '#f4f6f9',
  borderBottom: '1px solid #e3e8ef',
  overflowX: 'auto',
  position: 'sticky',
  top: 184,
  zIndex: 2,
};

const pillStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: '5px 12px',
  borderRadius: 16,
  border: '1px solid #e3e8ef',
  fontSize: 12,
  whiteSpace: 'nowrap',
};

const filterPanel: React.CSSProperties = {
  background: '#fff',
  padding: '12px 14px',
  borderBottom: '1px solid #e3e8ef',
};

const dayHeader: React.CSSProperties = {
  padding: '14px 14px 6px',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
};

const cardStyle: React.CSSProperties = {
  background: '#fff',
  margin: '6px 12px',
  borderRadius: 12,
  border: '1px solid #e3e8ef',
  padding: '12px 14px',
  transition: 'box-shadow 150ms, border-color 150ms',
};

const emptyDay: React.CSSProperties = {
  margin: '4px 12px 12px',
  padding: 14,
  textAlign: 'center',
  color: '#9ca3af',
  fontSize: 12,
  fontStyle: 'italic',
  background: 'rgba(255,255,255,0.5)',
  border: '1px dashed #e3e8ef',
  borderRadius: 10,
};

const emptyState: React.CSSProperties = {
  textAlign: 'center',
  color: '#9ca3af',
  padding: '40px 20px',
};

const fabStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 20,
  right: 20,
  background: '#2980b9',
  color: '#fff',
  width: 50,
  height: 50,
  borderRadius: '50%',
  border: 'none',
  boxShadow: '0 6px 16px rgba(41,128,185,0.4)',
  fontSize: 20,
  cursor: 'pointer',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const ghostBtn: React.CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  color: '#666',
  border: '1px solid #ccc',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
};

function actionBtn(color: string): React.CSSProperties {
  return {
    padding: '5px 12px',
    background: color,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
  };
}

const cardFooterStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginTop: 12,
  paddingTop: 10,
  borderTop: '1px solid #eef2f7',
};

const detailsAffordanceStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const primaryActionBtn: React.CSSProperties = {
  background: '#27ae60',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '8px 22px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  minWidth: 92,
  boxShadow: '0 1px 2px rgba(39,174,96,0.25)',
};

const secondaryActionBtn: React.CSSProperties = {
  background: '#fff',
  color: '#4a5568',
  border: '1px solid #d6dde6',
  borderRadius: 999,
  padding: '7px 21px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  minWidth: 92,
};

const fullBadge: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#c0392b',
  background: '#fdecea',
  border: '1px solid #f5c2bd',
  borderRadius: 999,
  padding: '6px 14px',
  minWidth: 92,
  textAlign: 'center',
};

const playerChip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: '#eef2f7',
  color: '#2c3e50',
  border: '1px solid #d6dde6',
  borderRadius: 14,
  padding: '3px 4px 3px 10px',
  fontSize: 13,
};

const chipRemoveBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#888',
  cursor: 'pointer',
  fontSize: 15,
  padding: '0 4px',
  lineHeight: 1,
};

const filterInput: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 13,
  borderRadius: 4,
  border: '1px solid #ccc',
  background: '#fff',
};

const dateFilterLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  fontSize: 11,
  color: '#6b7280',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const metaLabel: React.CSSProperties = {
  display: 'inline-block',
  minWidth: 70,
  color: '#6b7280',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontWeight: 600,
};
